import { apiFetch } from "@/shared/api/http";

export type AttendanceStatus = "Presente" | "Ausente" | "Justificado";

export interface ProfessorAulaHoje {
  turmaId: string;
  turmaNome: string;
  horarioTurmaId: string;
  diaSemana: string;
  horaInicio: string;
  horaFim: string;
  data: string;
  totalMatriculados: number;
  totalExperimentais: number;
  totalLancados: number;
  presentes: number;
  ausentes: number;
  justificados: number;
  chamadaIniciada: boolean;
  chamadaCompleta: boolean;
  sessaoId?: string | null;
  statusSessao?: string | null;
  iniciadaEmUtc?: string | null;
  encerradaEmUtc?: string | null;
  planoAulaId?: string | null;
  temaPlanejado?: string | null;
  objetivoPlanejado?: string | null;
  statusPlano?: "Rascunho" | "Planejada" | "RegistroPendente" | "Aplicada" | "Cancelada" | string | null;
}

export interface ChamadaAluno {
  alunoId: string;
  nome: string;
  fotoUrl?: string | null;
  status?: AttendanceStatus | null;
  observacao?: string | null;
  lancado: boolean;
  ausenciaInformada: boolean;
  motivoAusencia?: string | null;
}

export interface ChamadaExperimental {
  aulaExperimentalId: string;
  nome: string;
  telefone?: string | null;
  status: string;
  observacao?: string | null;
}

export interface ProfessorChamada {
  sessaoId?:string|null;statusSessao?:string|null;
  turmaId: string;
  turmaNome: string;
  horarioTurmaId: string;
  data: string;
  horaInicio: string;
  horaFim: string;
  alunos: ChamadaAluno[];
  experimentais: ChamadaExperimental[];
}

export function obterAulasProfessorHoje() {
  return apiFetch<ProfessorAulaHoje[]>("/api/app/professor/aulas/hoje");
}

export function obterAulasProfessorData(data: string) {
  return apiFetch<ProfessorAulaHoje[]>(`/api/app/professor/aulas?data=${encodeURIComponent(data)}`);
}

export function obterChamadaProfessor(aula: ProfessorAulaHoje) {
  const q = new URLSearchParams({
    turmaId: aula.turmaId,
    horarioTurmaId: aula.horarioTurmaId,
    data: aula.data
  });
  return apiFetch<ProfessorChamada>(`/api/app/professor/chamada?${q.toString()}`);
}

export function registrarChamadaProfessor(chamada: ProfessorChamada, statuses: Record<string, AttendanceStatus>, experimentais?: Array<{ aulaExperimentalId: string; compareceu: boolean }>) {
  return apiFetch<{ alunosProcessados: number; experimentaisProcessados: number }>("/api/app/professor/chamada", {
    method: "POST",
    body: JSON.stringify({
      turmaId: chamada.turmaId,
      horarioTurmaId: chamada.horarioTurmaId,
      data: chamada.data,
      presencas: chamada.alunos.map(aluno => ({
        alunoId: aluno.alunoId,
        status: statuses[aluno.alunoId] ?? aluno.status ?? "Ausente",
        observacao: aluno.observacao ?? null
      })),
      experimentais: experimentais ?? []
    })
  });
}


export function corrigirChamadaProfessor(chamada: ProfessorChamada, statuses: Record<string, AttendanceStatus>, motivo: string) {
  return apiFetch<{ alteracoes: number; totalProcessados: number }>("/api/app/professor/chamada/correcao", {
    method: "POST",
    body: JSON.stringify({
      turmaId: chamada.turmaId,
      horarioTurmaId: chamada.horarioTurmaId,
      data: chamada.data,
      motivo: motivo.trim(),
      presencas: chamada.alunos.map(aluno => ({
        alunoId: aluno.alunoId,
        status: statuses[aluno.alunoId] ?? aluno.status ?? "Ausente",
        observacao: aluno.observacao ?? null
      }))
    })
  });
}

export function iniciarAulaProfessor(aula: ProfessorAulaHoje) {
  return apiFetch<{ sessaoId: string; status: string }>("/api/app/professor/aulas/sessao/iniciar", {
    method: "POST",
    body: JSON.stringify({ turmaId: aula.turmaId, horarioTurmaId: aula.horarioTurmaId, data: aula.data })
  });
}

export function encerrarAulaProfessor(sessaoId: string) {
  return apiFetch<void>(`/api/app/professor/aulas/sessao/${sessaoId}/encerrar`, { method: "POST", body: "{}" });
}

export interface ProfessorGamificacaoResumo {
  periodo: string;
  turmas: number;
  alunosAtivos: number;
  frequenciaMedia: number;
  alunosAcima80: number;
  desafiosAtivos: number;
  destaques: Array<{ alunoId: string; nomeExibicao: string; treinos: number; frequenciaPercentual: number }>;
  desafios: Array<{ id:string; titulo:string; tipo:string; meta:number; dataFim:string; escopo:string }>;
  candidatosGraduacao: Array<{ alunoId:string; turmaId:string; nomeExibicao:string; faixaAtual:string; graus:number; proximaFaixa?:string|null; frequenciaPercentual:number; elegivel:boolean; observacao?:string|null }>;
}

export function obterGamificacaoProfessorResumo() {
  return apiFetch<ProfessorGamificacaoResumo>("/api/app/gamificacao/professor/resumo");
}

export type PlanoStatusExecucao = "Planejada" | "ConformePlanejado" | "Parcial" | "ConteudoDiferente" | "Cancelada";
export interface PlanoAulaMaterialApp { id:string; tipo:"Video"|"Imagem"|"Documento"|"Link"; titulo:string; url:string; visivelParaAluno:boolean; }
export interface PlanoAulaConteudoApp { recursoEnsinoId:string; nome:string; tema:string; categoria?:string|null; tipo:string; ensinado:boolean; }
export interface ProfessorPlanoAula {
  requerAssumir?:boolean;
  id:string; turmaId:string; horarioTurmaId?:string|null; data:string; tema:string; objetivo?:string|null;
  aquecimento?:string|null; tecnicas?:string|null; drills?:string|null; sparring?:string|null; observacoesPrivadas?:string|null;
  duracaoMinutos?:number|null; statusExecucao:PlanoStatusExecucao; temaMinistrado?:string|null; continuarNaProximaAula:boolean;
  materiais:PlanoAulaMaterialApp[]; conteudos:PlanoAulaConteudoApp[];
}
export function obterPlanoProfessor(aula: ProfessorAulaHoje) {
  const q = new URLSearchParams({turmaId:aula.turmaId,horarioTurmaId:aula.horarioTurmaId,data:aula.data});
  return apiFetch<ProfessorPlanoAula | null>(`/api/app/professor/plano-aula?${q.toString()}`);
}
export function finalizarPlanoProfessor(id:string, body:{status:PlanoStatusExecucao;temaMinistrado?:string;observacoesExecucao?:string;continuarNaProximaAula:boolean;resumoAluno?:string;conteudosEnsinadosIds?:string[]}) {
  return apiFetch<void>(`/api/app/professor/plano-aula/${id}/finalizar`, {method:"POST",body:JSON.stringify(body)});
}
export function assumirPlanoProfessor(id:string, motivo?:string|null) {
  return apiFetch<void>(`/api/app/ensino/planos/${id}/assumir`, {method:"POST",body:JSON.stringify({motivo:motivo?.trim()||null})});
}

export interface AppMomentoAulaMidia { momentoId:string; midiaId:string; contentType:string; ordem:number; }
export interface AppMomentoAula { id:string; legenda?:string|null; publicadoEmUtc:string; midias:AppMomentoAulaMidia[]; }
export function obterMomentosAulaProfessor(sessaoId:string) {
  return apiFetch<AppMomentoAula[]>(`/api/app/professor/aulas/${sessaoId}/momentos`);
}
export function publicarMomentoAulaProfessor(sessaoId:string, files:File[], legenda:string, participantes=true) {
  const form = new FormData();
  if (legenda.trim()) form.append("Legenda", legenda.trim());
  form.append("Visibilidade", participantes ? "Participantes" : "Equipe");
  files.forEach(file => form.append("Imagens", file));
  return apiFetch<string>(`/api/app/professor/aulas/${sessaoId}/momentos`, { method:"POST", body:form });
}
export function professorMomentoMidiaPath(momentoId:string, midiaId:string) {
  return `/api/app/professor/momentos/${momentoId}/midias/${midiaId}`;
}

export interface ProfessorEnsinoOptions {
  modalidades: Array<{id:string;nome:string}>;
  turmas: Array<{id:string;nome:string;modalidadeId:string;horarios:Array<{id:string;diaSemana:string;horaInicio:string}>}>;
  podeEditar: boolean;
  podePublicar: boolean;
}
export interface ProfessorEnsinoResource {
  id:string; modalidadeId:string; tipo:"Conteudo"|"Template"; nome:string; categoria:string|null; versao:string; podeEditar:boolean;
  conteudo:{tema:string;objetivo:string|null;tecnicas:string|null;aquecimento:string|null;drills:string|null;sparring:string|null;observacoes:string|null;duracaoMinutos:number|null;materiais:Array<{titulo:string;url:string;tipo:string;visivelParaAluno:boolean}>};
}
export interface ProfessorPlanEdit {
  id?:string|null; turmaId:string; data:string; horarioTurmaId?:string|null; versao?:string|null; recursosIds:string[];
  conteudo:{tema:string;objetivo:string|null;tecnicas:string|null;aquecimento:string|null;drills:string|null;sparring:string|null;observacoes:string|null;duracaoMinutos:number|null;materiais:Array<{titulo:string;url:string;tipo:string;visivelParaAluno:boolean}>};
}
const ensinoBase="/api/app/ensino/planos";
export const obterOpcoesPlanejamentoProfessor=()=>apiFetch<ProfessorEnsinoOptions>(`${ensinoBase}/opcoes`);
export const obterRecursosPlanejamentoProfessor=(modalidadeId:string)=>apiFetch<ProfessorEnsinoResource[]>(`${ensinoBase}/recursos?modalidadeId=${encodeURIComponent(modalidadeId)}`);
export const editarPlanejamentoProfessor=(id:string)=>apiFetch<ProfessorPlanEdit>(`${ensinoBase}/${id}/edicao`);
export interface ProfessorPreviousPlan { id:string; data:string; continuarNaProximaAula:boolean; conteudo:ProfessorPlanEdit["conteudo"]; recursosIds:string[]; }
export const obterPlanejamentoAnteriorProfessor=(turmaId:string,antesDe:string)=>apiFetch<ProfessorPreviousPlan|null>(`${ensinoBase}/anterior?turmaId=${encodeURIComponent(turmaId)}&antesDe=${encodeURIComponent(antesDe)}`);
export const salvarPlanejamentoProfessor=(payload:ProfessorPlanEdit)=>apiFetch<string>(`${ensinoBase}/guiado`,{method:"POST",body:JSON.stringify(payload)});
export const publicarPlanejamentoProfessor=(id:string)=>apiFetch<void>(`${ensinoBase}/${id}/publicar`,{method:"POST",body:"{}"});
export const excluirPlanejamentoProfessor=(id:string)=>apiFetch<void>(`${ensinoBase}/${id}`,{method:"DELETE"});
export function adicionarMaterialBibliotecaProfessor(recursoEnsinoId:string, material:{titulo:string;url:string;tipo:"Video"|"Imagem"|"Documento"|"Link";visivelParaAluno?:boolean}) {
  return apiFetch<void>(`${ensinoBase}/recursos/${encodeURIComponent(recursoEnsinoId)}/materiais`, {method:"POST",body:JSON.stringify(material)});
}
export function enviarVideoMaterialProfessor(planoId:string, input:{arquivo:File;titulo:string;recursoEnsinoId?:string|null;visivelParaAluno:boolean;salvarNaBiblioteca:boolean}) {
  const form=new FormData();
  form.append("Titulo",input.titulo.trim());
  if(input.recursoEnsinoId)form.append("RecursoEnsinoId",input.recursoEnsinoId);
  form.append("VisivelParaAluno",String(input.visivelParaAluno));
  form.append("SalvarNaBiblioteca",String(input.salvarNaBiblioteca));
  form.append("Arquivo",input.arquivo);
  return apiFetch<{titulo:string;url:string;tipo:"Video";visivelParaAluno:boolean}>(`${ensinoBase}/${encodeURIComponent(planoId)}/materiais/video`,{method:"POST",body:form});
}
export function materialEnsinoArquivoPath(url:string) {
  const match=/^yamarhub:\/\/material\/([0-9a-f]{32}|[0-9a-f-]{36})$/i.exec(url.trim());
  return match?`/api/app/ensino/materiais/arquivos/${match[1]}`:null;
}

export interface ProfessorCalendarioDia {
  data: string;
  totalAulas: number;
  planejadas: number;
  semPlanejamento: number;
  totalCompeticoes: number;
}

export interface ProfessorCompeticaoDia {
  campeonatoId: string;
  nome: string;
  data: string;
  local?: string | null;
  modalidadeId: string;
  modalidade: string;
  inscritos: number;
}
export function obterCalendarioProfessorMes(ano:number, mes:number) {
  return apiFetch<ProfessorCalendarioDia[]>(`/api/app/professor/calendario?ano=${ano}&mes=${mes}`);
}

export function obterCompeticoesProfessorData(data:string) {
  return apiFetch<ProfessorCompeticaoDia[]>(`/api/app/professor/competicoes?data=${encodeURIComponent(data)}`);
}

export interface ProfessorCompeticaoAtleta {
  inscricaoId:string;
  alunoId:string;
  nome:string;
  faixa?:string|null;
  categoria:string;
  categoriaPeso?:string|null;
  status:"Inscrito"|"PagamentoPendente"|"Confirmado"|string;
  inscricaoExternaConfirmada:boolean;
  inscricaoExternaConfirmadaEmUtc?:string|null;
  participacaoConfirmada:boolean;
  lutasRealizadas:number;
  vitorias:number;
  resultado?:string|null;
  colocacao?:number|null;
}
export interface ProfessorCompeticaoDetalhe {
  campeonatoId:string;
  nome:string;
  data:string;
  local?:string|null;
  modalidade:string;
  atletas:ProfessorCompeticaoAtleta[];
}
export function obterCompeticaoProfessor(campeonatoId:string) {
  return apiFetch<ProfessorCompeticaoDetalhe>(`/api/app/professor/competicoes/${encodeURIComponent(campeonatoId)}`);
}

export interface ProfessorMomentoHistorico {
  id:string;
  sessaoId:string;
  turmaId:string;
  turmaNome:string;
  data:string;
  horaInicio:string;
  legenda?:string|null;
  publicadoEmUtc:string;
  visibilidade:string;
  midias:AppMomentoAulaMidia[];
}
export function obterMomentosProfessor(take=30) {
  return apiFetch<ProfessorMomentoHistorico[]>(`/api/app/professor/momentos?take=${take}`);
}


export interface ProfessorAlunoHistorico {
  presencaId:string;
  data:string;
  turma:string;
  status:AttendanceStatus|string;
  observacao?:string|null;
}
export interface ProfessorAlunoResumo {
  alunoId:string;
  nome:string;
  fotoUrl?:string|null;
  ativo:boolean;
  dataNascimento:string;
  totalRegistros:number;
  presentes:number;
  ausentes:number;
  justificados:number;
  frequenciaPercentual:number;
  sequenciaFaltas:number;
  alerta?:string|null;
  historico:ProfessorAlunoHistorico[];
}
export function obterResumoAlunoProfessor(turmaId:string, alunoId:string, dias=60) {
  const q=new URLSearchParams({turmaId,dias:String(dias)});
  return apiFetch<ProfessorAlunoResumo>(`/api/app/professor/alunos/${encodeURIComponent(alunoId)}/resumo?${q.toString()}`);
}
export function registrarObservacaoAlunoProfessor(aula:ProfessorAulaHoje, alunoId:string, observacao:string, compartilharComAluno:boolean, eventoId:string) {
  return apiFetch<void>(`/api/app/professor/alunos/${encodeURIComponent(alunoId)}/observacao`, {
    method:"POST",
    body:JSON.stringify({eventoId,turmaId:aula.turmaId,horarioTurmaId:aula.horarioTurmaId,data:aula.data,observacao,compartilharComAluno})
  });
}

export interface ProfessorAlunoAtencao {
  alunoId:string;turmaId:string;nome:string;turma:string;fotoUrl?:string|null;ultimoTreino?:string|null;diasSemTreinar:number;frequenciaPercentual:number;motivo:string;
}
export interface ProfessorAlunoNovo {
  alunoId:string;turmaId:string;nome:string;turma:string;fotoUrl?:string|null;dataInicio:string;diasNaTurma:number;
}
export interface ProfessorExperimentalProximo {
  aulaExperimentalId:string;turmaId:string;nome:string;turma:string;dataHoraAula:string;status:string;
}
export interface ProfessorTurmaHistorico {
  turmaId:string;horarioTurmaId:string;turma:string;data:string;horaInicio:string;presentes:number;ausentes:number;justificados:number;tema?:string|null;
}
export interface ProfessorOperacaoHome {
  atencao:ProfessorAlunoAtencao[];novos:ProfessorAlunoNovo[];experimentais:ProfessorExperimentalProximo[];historicoTurmas:ProfessorTurmaHistorico[];
}
export function obterOperacaoProfessorResumo(){return apiFetch<ProfessorOperacaoHome>("/api/app/professor/operacao/resumo");}

export interface ProfessorEvolutionSignal {
  alunoId:string;turmaId:string;nome:string;turma:string;fotoUrl?:string|null;tipo:"Frequencia"|"Graduacao"|"NovoAluno"|string;titulo:string;descricao:string;prioridade:number;frequenciaPercentual:number;ultimoTreino?:string|null;faixaAtual?:string|null;graus?:number|null;proximaFaixa?:string|null;
}
export interface ProfessorEvolutionHome {
  atencao:ProfessorEvolutionSignal[];alunos:ProfessorEvolutionStudent[];totalAlunosAcompanhados:number;podeAvaliarGraduacao:boolean;
}
export function obterEvolucaoProfessorResumo(){return apiFetch<ProfessorEvolutionHome>("/api/app/professor/evolucao/resumo");}
export interface ProfessorEvolutionStudentPage { itens:ProfessorEvolutionStudent[];pagina:number;tamanhoPagina:number;total:number;temMais:boolean; }
export function buscarAlunosEvolucaoProfessor(busca:string,pagina=1,tamanhoPagina=20,turmaId?:string){const q=new URLSearchParams({busca,pagina:String(pagina),tamanhoPagina:String(tamanhoPagina)});if(turmaId)q.set("turmaId",turmaId);return apiFetch<ProfessorEvolutionStudentPage>(`/api/app/professor/evolucao/alunos?${q.toString()}`);}

export function enviarAvisoTurmaProfessor(aula:ProfessorAulaHoje, eventoId:string, titulo:string, mensagem:string) {
  return apiFetch<{alunosDestinatarios:number}>(`/api/app/professor/turmas/${aula.turmaId}/avisos`, {
    method:"POST",
    body:JSON.stringify({ eventoId, horarioTurmaId:aula.horarioTurmaId, data:aula.data, titulo:titulo.trim(), mensagem:mensagem.trim() })
  });
}

export interface ProfessorTechnicalCriterion { chave:string; nome:string; ordem:number; categoria:string; peso:number; essencial:boolean; valorMinimo:number; descricao?:string|null; perfilEtario:string; competenciaChave?:string|null; tecnicaChave?:string|null; tecnicaNome?:string|null; }
export interface ProfessorTechnicalEvaluationRequestItem { chave:string; valor:number; }
export interface ProfessorTechnicalEvaluationItem { chave:string; nome:string; valor:number; }
export interface ProfessorTechnicalEvaluationPoint {
  avaliacaoId:string; data:string; turma:string; professor:string; media:number; compartilhada:boolean; observacao?:string|null; itens:ProfessorTechnicalEvaluationItem[]; tipoRegistro:string; estadoRapido?:string|null; temaAula?:string|null; tecnica?:string|null;
}
export interface ProfessorTechnicalReadiness { status:string; mediaPonderada:number; avaliacoesConsideradas:number; pontosAtencao:string[]; mensagem:string; }
export interface ProfessorTechnicalEvolutionSummary {
  alunoId:string; aluno:string; modalidadeId:string; modalidade:string; perfilEtario:string; criterios:ProfessorTechnicalCriterion[]; avaliacoes:ProfessorTechnicalEvaluationPoint[]; prontidao?:ProfessorTechnicalReadiness|null;
}
export interface ProfessorTechnicalContext { origem:string; tema?:string|null; tecnica?:string|null; criteriosSugeridos:ProfessorTechnicalCriterion[]; permiteModoRapido:boolean; }
export function obterContextoEvolucaoProfessor(aula:ProfessorAulaHoje, alunoId:string, tecnica?:string) {
  const q=new URLSearchParams({alunoId,horarioTurmaId:aula.horarioTurmaId,data:aula.data});
  if(tecnica?.trim())q.set("tecnica",tecnica.trim());
  return apiFetch<ProfessorTechnicalContext>(`/api/app/professor/turmas/${encodeURIComponent(aula.turmaId)}/evolucao-tecnica/contexto?${q.toString()}`);
}
export function obterCriteriosEvolucaoProfessor(turmaId:string, alunoId:string, opts?:{horarioTurmaId?:string;data?:string;tecnica?:string;tema?:string}) {
  const q=new URLSearchParams({alunoId});
  if(opts?.horarioTurmaId)q.set("horarioTurmaId",opts.horarioTurmaId);
  if(opts?.data)q.set("data",opts.data);
  if(opts?.tecnica?.trim())q.set("tecnica",opts.tecnica.trim());
  if(opts?.tema?.trim())q.set("tema",opts.tema.trim());
  return apiFetch<ProfessorTechnicalCriterion[]>(`/api/app/professor/turmas/${encodeURIComponent(turmaId)}/evolucao-tecnica/criterios?${q.toString()}`);
}
export function obterEvolucaoTecnicaProfessor(turmaId:string, alunoId:string, meses=6) {
  const q=new URLSearchParams({turmaId,meses:String(meses)});
  return apiFetch<ProfessorTechnicalEvolutionSummary>(`/api/app/professor/alunos/${encodeURIComponent(alunoId)}/evolucao-tecnica?${q.toString()}`);
}
export function registrarEvolucaoTecnicaProfessor(aula:ProfessorAulaHoje, alunoId:string, eventoId:string, compartilhada:boolean, observacao:string|undefined, itens:ProfessorTechnicalEvaluationRequestItem[], contexto?:{temaAula?:string|null;tecnica?:string|null}) {
  return apiFetch<string>(`/api/app/professor/alunos/${encodeURIComponent(alunoId)}/evolucao-tecnica`, {
    method:"POST",
    body:JSON.stringify({eventoId,turmaId:aula.turmaId,horarioTurmaId:aula.horarioTurmaId,data:aula.data,compartilhada,observacao:observacao?.trim()||null,itens,temaAula:contexto?.temaAula??null,tecnica:contexto?.tecnica??null})
  });
}
export function registrarEvolucaoTecnicaPerfilProfessor(turmaId:string, alunoId:string, data:string, eventoId:string, compartilhada:boolean, observacao:string|undefined, itens:ProfessorTechnicalEvaluationRequestItem[], contexto?:{horarioTurmaId?:string|null;temaAula?:string|null;tecnica?:string|null}) {
  return apiFetch<string>(`/api/app/professor/alunos/${encodeURIComponent(alunoId)}/evolucao-tecnica`, {
    method:"POST",
    body:JSON.stringify({eventoId,turmaId,horarioTurmaId:contexto?.horarioTurmaId??null,data,compartilhada,observacao:observacao?.trim()||null,itens,temaAula:contexto?.temaAula??null,tecnica:contexto?.tecnica??null})
  });
}
export type ProfessorQuickTechnicalState="EmDesenvolvimento"|"ExecutouBem";
export interface ProfessorQuickTechnicalEvaluation { alunoId:string;estado:ProfessorQuickTechnicalState;registradaEmUtc:string; }
export function obterEvolucaoRapidaProfessor(aula:ProfessorAulaHoje) {
  const q=new URLSearchParams({horarioTurmaId:aula.horarioTurmaId,data:aula.data});
  return apiFetch<ProfessorQuickTechnicalEvaluation[]>(`/api/app/professor/turmas/${encodeURIComponent(aula.turmaId)}/evolucao-tecnica/rapida?${q.toString()}`);
}
export function registrarEvolucaoRapidaProfessor(aula:ProfessorAulaHoje, alunos:Array<{eventoId:string;alunoId:string;estado:ProfessorQuickTechnicalState}>, contexto?:{temaAula?:string|null;tecnica?:string|null}, compartilhada=true) {
  return apiFetch<{registradas:number;ignoradas:number}>(`/api/app/professor/turmas/${encodeURIComponent(aula.turmaId)}/evolucao-tecnica/rapida`, {
    method:"POST",
    body:JSON.stringify({horarioTurmaId:aula.horarioTurmaId,data:aula.data,temaAula:contexto?.temaAula??null,tecnica:contexto?.tecnica??null,compartilhada,alunos})
  });
}


export interface ProfessorGraduationEvent {
  id:string; nome:string; dataEvento:string; local?:string|null; status:string; turmaId:string; alunoJaSelecionado:boolean; statusProcesso?:string|null;
  destinosValidos:string[]; faixaDestinoSelecionada?:string|null;
}
export interface ProfessorGraduationSelectionResult { processoId:string; eventoId:string; alunoId:string; faixaDestino:string; status:string; jaExistia:boolean; }
export function obterEventosGraduacaoProfessor(turmaId:string, alunoId?:string) {
  const q=new URLSearchParams({turmaId}); if(alunoId)q.set("alunoId",alunoId);
  return apiFetch<ProfessorGraduationEvent[]>(`/api/app/professor/graduacoes/eventos?${q.toString()}`);
}
export function selecionarAlunoEventoGraduacaoProfessor(eventoId:string, alunoId:string, turmaId:string, faixaDestino:string) {
  return apiFetch<ProfessorGraduationSelectionResult>(`/api/app/professor/graduacoes/eventos/${encodeURIComponent(eventoId)}/alunos/${encodeURIComponent(alunoId)}/selecionar`,{method:"POST",body:JSON.stringify({turmaId,faixaDestino})});
}

export interface ProfessorGraduationMobileClass { turmaId:string;turma:string;modalidadeId:string;modalidade:string;totalAlunos:number; }
export interface ProfessorGraduationMobileStudent { alunoId:string;nome:string;fotoUrl?:string|null;faixaAtual?:string|null;graus:number;frequenciaPercentual:number;mesesNaFaixa:number;elegivelParaAvaliacao:boolean;proximaFaixa?:string|null;destinosValidos:string[];sinal:string;jaSelecionado:boolean; }
export interface ProfessorGraduationMobileStudentPage { itens:ProfessorGraduationMobileStudent[];pagina:number;tamanhoPagina:number;total:number;temMais:boolean; }
export interface ProfessorGraduationMobileEvent { eventoId:string;nome:string;dataEvento:string;local?:string|null;turmaId:string;status:string; }
export interface ProfessorGraduationMobileActiveEvent { eventoId:string;nome:string;dataEvento:string;local?:string|null;status:string;turmaId:string;turma:string;modalidade:string;totalSelecionados:number; }
export interface ProfessorGraduationMobileConfirmation { eventoId:string;solicitados:number;selecionados:number;jaSelecionados:number; }
export function obterTurmasGraduacaoMobileProfessor(){return apiFetch<ProfessorGraduationMobileClass[]>("/api/app/professor/graduacoes/mobile/turmas");}
export function obterEventosGraduacaoMobileProfessor(){return apiFetch<ProfessorGraduationMobileActiveEvent[]>("/api/app/professor/graduacoes/mobile/eventos");}
export function obterAlunosGraduacaoMobileProfessor(turmaId:string,eventoId?:string,dataReferencia?:string,busca="",pagina=1,tamanhoPagina=20){const q=new URLSearchParams({turmaId,pagina:String(pagina),tamanhoPagina:String(tamanhoPagina)});if(eventoId)q.set("eventoId",eventoId);if(dataReferencia)q.set("dataReferencia",dataReferencia);if(busca.trim())q.set("busca",busca.trim());return apiFetch<ProfessorGraduationMobileStudentPage>(`/api/app/professor/graduacoes/mobile/alunos?${q.toString()}`);}
export function criarGraduacaoMobileProfessor(input:{turmaId:string;dataEvento:string;nome?:string|null;local?:string|null}){return apiFetch<ProfessorGraduationMobileEvent>("/api/app/professor/graduacoes/mobile",{method:"POST",body:JSON.stringify(input)});}
export interface ProfessorGraduationMobileSelection { alunoId:string;faixaDestino:string; }
export function confirmarGraduacaoMobileProfessor(eventoId:string,turmaId:string,alunos:ProfessorGraduationMobileSelection[]){return apiFetch<ProfessorGraduationMobileConfirmation>(`/api/app/professor/graduacoes/mobile/${encodeURIComponent(eventoId)}/confirmar`,{method:"POST",body:JSON.stringify({turmaId,alunos})});}

export interface ProfessorEvolutionStudent {
  alunoId:string;turmaId:string;nome:string;turma:string;fotoUrl?:string|null;faixaAtual?:string|null;graus?:number|null;
}
export interface ProfessorSportsProfileCompetition {
  campeonatoId:string;campeonato:string;data:string;modalidade?:string|null;participacaoConfirmada:boolean;lutasRealizadas:number;vitorias:number;resultado?:string|null;colocacao?:number|null;
}
export interface ProfessorSportsProfileHistoryItem { tipo:string;data:string;titulo:string;descricao?:string|null; }
export interface ProfessorSportsProfileActions { podeAvaliarTecnica:boolean;podeAvaliarGraduacao:boolean;temCompeticoes:boolean; }
export interface ProfessorSportsProfileContextualEvaluation { presencaId:string;data:string;horarioTurmaId?:string|null;origem:string;tema?:string|null;tecnica?:string|null;criteriosSugeridos:ProfessorTechnicalCriterion[];jaPossuiAvaliacao:boolean; }
export interface ProfessorSportsProfileGraduationHistory { id:string;tipo:string;faixa:string;grau:number;data:string;professor:string;observacao?:string|null; }
export interface ProfessorSportsProfileGraduationEligibility { proximaFaixa?:string|null;mesesNaFaixa:number;tempoMinimoAtingido:boolean;idadeAtual:number;idadeMinimaAtingida:boolean;frequenciaPercentual:number;frequenciaMinimaAtingida:boolean;quatroGrausAtingidos:boolean;elegivelParaAvaliacaoFaixa:boolean;observacao?:string|null; }
export interface ProfessorSportsProfileGraduation { alunoId:string;aluno:string;faixaAtual?:string|null;grausAtuais:number;ultimaProgressao?:string|null;historico:ProfessorSportsProfileGraduationHistory[];elegibilidade?:ProfessorSportsProfileGraduationEligibility|null; }
export interface ProfessorSportsProfileFederation { organizacao:string;tipo:string;numeroRegistro?:string|null;dataValidade?:string|null;situacao:string; }
export interface ProfessorSportsProfileIdentification { dataNascimento:string;idade:number;modalidadeId:string;modalidade:string;filiacoes:ProfessorSportsProfileFederation[]; }
export interface ProfessorSportsProfile {
  alunoId:string;turmaId:string;turma:string;nome:string;fotoUrl?:string|null;identificacao:ProfessorSportsProfileIdentification;
  frequencia:ProfessorAlunoResumo;
  tecnica:ProfessorTechnicalEvolutionSummary;
  graduacao:ProfessorSportsProfileGraduation;
  competicoes:ProfessorSportsProfileCompetition[];
  historico:ProfessorSportsProfileHistoryItem[];
  avaliacaoContextual?:ProfessorSportsProfileContextualEvaluation|null;
  acoes:ProfessorSportsProfileActions;
}
export function obterPerfilEsportivoProfessor(turmaId:string,alunoId:string){
  const q=new URLSearchParams({turmaId});
  return apiFetch<ProfessorSportsProfile>(`/api/app/professor/alunos/${encodeURIComponent(alunoId)}/perfil-esportivo?${q.toString()}`);
}

export function retirarAlunoGraduacaoMobileProfessor(eventoId:string,turmaId:string,alunoId:string,motivo:string){return apiFetch<void>(`/api/app/professor/graduacoes/mobile/${encodeURIComponent(eventoId)}/alunos/${encodeURIComponent(alunoId)}/retirar`,{method:"POST",body:JSON.stringify({turmaId,motivo})});}
